<template>
  <v-menu
    v-model="contextMenuModel"
    :target="[positionX, positionY]"
    :close-on-content-click="false"
    :persistent="false"
    location="bottom"
    absolute
    z-index="2001"
    style="z-index: 2001 !important;"
  >
    <v-list v-if="node" style="overflow-y:auto;" data-cy="nodeSideMenu-treeItemContext-list">

      <v-list-item
        v-if="showingCreateClient"
        @click="openCreateClientDialog"
        data-cy="nodeSideMenu-treeItemContext-createClient"
        prepend-icon="tw tw-office-building-plus"
      >
        <v-list-item-title>
          {{ $t('menus.createNode.createClient') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="showingCreateProject"
        @click="openCreateProjectDialog"
        data-cy="nodeSideMenu-treeItemContext-createProject"
        prepend-icon="mdi-briefcase-plus"
      >
        <v-list-item-title>
          {{ $t('menus.createNode.createProject') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="showingCreateFolder"
        @click="openCreateFolderDialog"
        data-cy="nodeSideMenu-treeItemContext-createFolder"
        prepend-icon="mdi-folder-plus"
      >
        <v-list-item-title>
          {{ $t('menus.createNode.createFolder') }}
        </v-list-item-title>
      </v-list-item>

      <v-divider v-if="showingCreateClient || showingCreateProject || showingCreateFolder" aria-hidden="true" />

      <v-list-item
        v-if="!isRootNode && $canControlNodeAndPod()"
        @click="openEditNodeDialog"
        data-cy="nodeSideMenu-treeItemContext-updateNode"
        prepend-icon="mdi-folder-edit-outline"
      >
        <v-list-item-title>
          {{ $t('menus.createNode.editFolder') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="!isRootNode && $canControlNodeAndPod()"
        @click="openRenameNodeDialog"
        data-cy="nodeSideMenu-treeItemContext-renameNode"
        prepend-icon="mdi-rename-box-outline"
      >
        <v-list-item-title>
          {{ $t('routes.node.renameFolder') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="!$isAdobeExtension() && !$isFCPExtension() && $canDownloadNodeAndPod()"
        @click="openDownloadDialog"
        data-cy="nodeSideMenu-treeItemContext-downloadSelected"
        prepend-icon="mdi-folder-download-outline"
      >
        <v-list-item-title>
          {{ $t('routes.node.downloadNode') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="$canUploadFileAndAsset()"
        @click="openUploadFilesDialog"
        data-cy="nodeSideMenu-treeItemContext-uploadFiles"
        prepend-icon="mdi-upload"
      >
        <v-list-item-title>
          {{ $t('menus.tools.uploadFiles') }}
        </v-list-item-title>
      </v-list-item>

      <v-divider v-if="!isRootNode" aria-hidden="true" />

      <v-list-item
        v-if="!isRootNode && $canControlNodeAndPod()"
        @click="openCopySelectedDialog"
        data-cy="nodeSideMenu-treeItemContext-copySelected"
        prepend-icon="mdi-checkbox-multiple-marked-outline"
      >
        <v-list-item-title>
          {{ $t('routes.node.copyNode') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="!isRootNode && $canControlNodeAndPod()"
        @click="openMoveSelectedDialog"
        data-cy="nodeSideMenu-treeItemContext-moveSelected"
        prepend-icon="mdi-checkbox-multiple-marked"
      >
        <v-list-item-title>
          {{ $t('routes.node.moveNode') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="!isRootNode && $canDeleteNodeAndPod() && !isCurrentNodeTenant"
        @click="openDeleteNodeDialog"
        data-cy="nodeSideMenu-treeItemContext-deleteNode"
        prepend-icon="mdi-trash-can-outline"
      >
        <v-list-item-title>
          {{ $t('menus.tools.deleteNode') }}
        </v-list-item-title>
      </v-list-item>

      <v-divider v-if="!isRootNode" aria-hidden="true" />

      <v-list-item
        v-if="$isTeamUser() && $canControlNodeAndPod()"
        @click="toggleTeamonly()"
        data-cy="node-file-list-teamOnly"
        :prepend-icon="node.teamonly ? 'mdi-folder-lock-open' : 'mdi-folder-lock-outline'"
      >
        <v-list-item-title>
          {{ node.teamonly ? $t('routes.node.removeTeamOnlyAccess') : $t('routes.node.setTeamOnlyAccess') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="!isRootNode && !node.archived && $canControlNodeAndPod() && $canArchiveNodeAndPod()"
        @click="openArchiveDialog"
        data-cy="nodeSideMenu-treeItemContext-archiveSelected"
        prepend-icon="mdi-archive-arrow-down-outline"
      >
        <v-list-item-title>
          {{ $t('menus.tools.archiveSelected') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="!isRootNode && node.archived && $canControlNodeAndPod() && $canArchiveNodeAndPod()"
        @click="openUnarchiveDialog"
        data-cy="nodeSideMenu-treeItemContext-unarchiveSelected"
        prepend-icon="mdi-archive-arrow-up-outline"
      >
        <v-list-item-title>
          {{ $t('menus.tools.unarchiveSelected') }}
        </v-list-item-title>
      </v-list-item>

      <v-divider aria-hidden="true" />

      <v-list-item
        @click="openNodePropertiesDialog"
        data-cy="nodeSideMenu-treeItemContext-nodeProperties"
        prepend-icon="mdi-information-outline"
      >
        <v-list-item-title>
          {{ $t('routes.node.propertiesNodeDialogTitle') }}
        </v-list-item-title>
      </v-list-item>

    </v-list>
  </v-menu>

  <div>
    <CreateNodeDialog
      v-if="showingCreateClient"
      :nodeType="nodeTypes.CLIENT"
      :parentNode="node"
      v-model="showCreateClientDialog"
    />

    <CreateNodeDialog
      v-if="showingCreateProject"
      :nodeType="nodeTypes.PROJECT"
      :parentNode="node"
      v-model="showCreateProjectDialog"
    />

    <CreateNodeDialog
      v-if="showingCreateFolder"
      :nodeType="nodeTypes.FOLDER"
      :parentNode="node"
      v-model="showCreateFolderDialog"
    />

    <CreateNodeDialog
      v-if="!isRootNode && $canControlNodeAndPod()"
      :editNodeId="node.id"
      :nodeType="node.type"
      v-model="showEditNodeDialog"
    />

    <RenameNodeDialog
      v-if="!isRootNode && $canControlNodeAndPod()"
      :node="node"
      v-model="showRenameNodeDialog"
    />

    <DownloadDialog
      v-if="!$isAdobeExtension() && !$isFCPExtension() && $canDownloadNodeAndPod()"
      :nodes="Array(node)"
      v-model="showDownloadDialog"
    />

    <UploadFilesDialog
      v-if="$canUploadFileAndAsset()"
      :node="node"
      v-model="showUploadFilesDialog"
    />

    <CopySelectedDialog
      v-if="!isRootNode && $canControlNodeAndPod()"
      :nodes="[node]"
      :pods="[]"
      v-model="showCopySelectedDialog"
    />

    <MoveSelectedDialog
      v-if="!isRootNode && $canControlNodeAndPod()"
      :nodes="[node]"
      :pods="[]"
      v-model="showMoveSelectedDialog"
    />

    <DeleteNodeDialog
      v-if="!isRootNode && $canDeleteNodeAndPod() && !isCurrentNodeTenant"
      :node="node"
      v-model="showDeleteNodeDialog"
    />

    <ArchiveSelectedDialog
      v-if="!isRootNode && !node.archived && $canControlNodeAndPod() && $canArchiveNodeAndPod()"
      :action="'archive'"
      :nodes="Array(node)"
      :pods="Array()"
      v-model="showArchiveDialog"
    />

    <NodePropertiesDialog
      :node="node"
      v-model="showNodePropertiesDialog"
    />
  </div>

</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useAuthStore } from '@/stores/AuthStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import { useCommonStore } from '@/stores/CommonStore.js';
import CreateNodeDialog from '../../../../layouts2/mainLayout/components/nodeSidemenu/components/dialogs/CreateNodeDialog.vue';
import DeleteNodeDialog from '../../../../layouts2/mainLayout/components/nodeSidemenu/components/dialogs/DeleteNodeDialog.vue';
import DownloadDialog from '../dialogs/DownloadDialog.vue';
import UploadFilesDialog from '../dialogs/UploadFilesDialog.vue';
import MoveSelectedDialog from '../dialogs/MoveSelectedDialog.vue';
import CopySelectedDialog from '../dialogs/CopySelectedDialog.vue';
import ArchiveSelectedDialog from '../dialogs/ArchiveSelectedDialog.vue';
import RenameNodeDialog from '../dialogs/RenameNodeDialog.vue';
import NodePropertiesDialog from '../dialogs/NodePropertiesDialog.vue';

export default {
  emits: ['update:modelValue'],
  name: "NodeContextMenu",

  components: {
    DownloadDialog,
    CreateNodeDialog,
    RenameNodeDialog,
    DeleteNodeDialog,
    UploadFilesDialog,
    MoveSelectedDialog,
    CopySelectedDialog,
    ArchiveSelectedDialog,
    NodePropertiesDialog,
  },

  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    positionX: {
      type: Number,
      required: true,
    },
    positionY: {
      type: Number,
      required: true,
    },
    node: {
      type: Object,
      // required: true,
    },
  },

  data() {
    return {
      showCreateClientDialog: false,
      showCreateProjectDialog: false,
      showCreateFolderDialog: false,
      showEditNodeDialog: false,
      showDownloadDialog: false,
      showUploadFilesDialog: false,
      showCopySelectedDialog: false,
      showMoveSelectedDialog: false,
      showDeleteNodeDialog: false,
      showArchiveDialog: false,
      showRenameNodeDialog: false,
      showNodePropertiesDialog: false,
    };
  },

  watch: {
    nodeFromState() {
      if (this.nodeFromState && this.nodeFromState.archived !== this.archiveStatus.NOTARCHIVED) {
        this.contextMenuModel = false;
      }
    },
  },

  methods: {
    openCreateClientDialog() {
      this.contextMenuModel = false;
      this.$nextTick(() => {
        this.showCreateClientDialog = true;
      });
    },
    openCreateProjectDialog() {
      this.contextMenuModel = false;
      this.$nextTick(() => {
        this.showCreateProjectDialog = true;
      });
    },
    openCreateFolderDialog() {
      this.contextMenuModel = false;
      this.$nextTick(() => {
        this.showCreateFolderDialog = true;
      });
    },
    openEditNodeDialog() {
      this.contextMenuModel = false;
      this.$nextTick(() => {
        this.showEditNodeDialog = true;
      });
    },
    openRenameNodeDialog() {
      this.contextMenuModel = false;
      this.$nextTick(() => {
        this.showRenameNodeDialog = true;
      });
    },
    openDownloadDialog() {
      this.contextMenuModel = false;
      this.$nextTick(() => {
        this.showDownloadDialog = true;
      });
    },
    openUploadFilesDialog() {
      this.contextMenuModel = false;
      this.$nextTick(() => {
        this.showUploadFilesDialog = true;
      });
    },
    openCopySelectedDialog() {
      this.contextMenuModel = false;
      this.$nextTick(() => {
        this.showCopySelectedDialog = true;
      });
    },
    openMoveSelectedDialog() {
      this.contextMenuModel = false;
      this.$nextTick(() => {
        this.showMoveSelectedDialog = true;
      });
    },
    openDeleteNodeDialog() {
      this.contextMenuModel = false;
      this.$nextTick(() => {
        this.showDeleteNodeDialog = true;
      });
    },
    openArchiveDialog() {
      this.contextMenuModel = false;
      this.$nextTick(() => {
        this.showArchiveDialog = true;
      });
    },
    openNodePropertiesDialog() {
      this.contextMenuModel = false;
      this.$nextTick(() => {
        this.showNodePropertiesDialog = true;
      });
    },
    toggleTeamonly() {
      const nodeData = {
        id: this.node.id,
        teamonly: !this.node.teamonly ? 1 : 0,
      };

      const nodeStore = useNodeStore();
      nodeStore.updateTeamOnly(nodeData)
        .then(() => {
          this.$showMessage(this.$t('routes.pod.teamonlyAccessUpdated'), { type: 'success' });
        })
        .catch((err) => {
          console.error(err);
          if (err && err?.response?.data?.error) {
            const errorMessage = this.$localizeMessage(err?.response?.data?.error, this.$t('common.generalError'));
            this.$showMessage(errorMessage, { type: 'error' });
          } else {
            console.log(err);
          }
        });

      this.contextMenuModel = false;
    },
  },

  computed: {
    contextMenuModel: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },
    nodeTypes() {
      const commonStore = useCommonStore();
      return commonStore.nodeTypes;
    },
    rootFolderId() {
      const commonStore = useCommonStore();
      return commonStore.rootFolderId;
    },
    archiveStatus() {
      const commonStore = useCommonStore();
      return commonStore.archiveStatus;
    },
    nodeFromState() {
      if (!this.node || !this.node.id) {
        return null;
      }

      // This is the node from the state manager.
      // The node from the props is from event and does not have watch events.
      const nodeStore = useNodeStore();
      return nodeStore.getNodeById(this.node.id);
    },
    showingCreateFolder() {
      return this.$canControlNodeAndPod();
    },
    showingCreateProject() {
      // return  this.$canControlNodeAndPod() &&
      //         this.node &&
      //         (this.node.type == this.nodeTypes.CLIENT ||
      //         this.node.type == this.nodeTypes.TENANT);
      return this.$canControlNodeAndPod();
    },
    showingCreateClient() {
      return this.$canControlNodeAndPod() &&
        this.node &&
        this.node.type == this.nodeTypes.TENANT;
    },
    isCurrentNodeTenant() {
      // return this.node.type === this.nodeTypes.TENANT;
      return this.node.parentid === this.rootFolderId && this.node.type === this.nodeTypes.TENANT;
    },
    currentNodeId() {
      const nodeStore = useNodeStore();
      return nodeStore.getCurrentNodeId;
    },
    hasSelectedItems() {
      const nodeStore = useNodeStore();
      const podStore = usePodStore();

      return nodeStore.hasSelectedNodes || podStore.hasSelectedPods;
    },
    isRootNode() {
      const authStore = useAuthStore();
      return this.node.id === authStore.rootNodeId;
    },
  }
};
</script>
